import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { useScopeOfWork } from "../context/ScopeOfWorkContext";
import { ScopeOfWorkHandle } from "./scope-of-work.handle";
import { ScopeOfWorkContent } from "../components/ScopeOfWorkContent";
import { ScopeOfWorkStep } from "../context/scope-of-work.types";
import { scopeOfWorkStepValidator } from "../validators/scope-of-work-validators";

import { fetchSpecialRequests } from "@components/quotes/quote-create-stepper/advanced-quote/sectorworkflow/components/orchestrator/special-request/actions/fetchSpecialRequests.actions";

interface Props {
  onStepChange?: (
    step: ScopeOfWorkStep,
    isLastStep: boolean,
  ) => void;
  onStateChange?: (state: any) => void;
  showControls?: boolean;
  debugJsonPayload: boolean;
  lockCategory?: boolean;
  showSummary?: boolean;
  job?: any;
  jobAlias?: string;
  setJobAlias?: (val: string) => void;
  jobDescription?: string;
  setJobDescription?: (val: string) => void;
}

export const ScopeOfWorkController = forwardRef<
  ScopeOfWorkHandle,
  Props
>(
  (
    {
      lockCategory,
      onStepChange,
      onStateChange,
      showControls,
      debugJsonPayload,
      showSummary = false,
      job,
      jobAlias,
      setJobAlias,
      jobDescription,
      setJobDescription,
    },
    ref,
  ) => {
    const { state, dispatch } = useScopeOfWork();

    const prevStateRef = useRef<string | null>(null);

    const categoryParam =
      state.category?.uid ??
      state.category?.name ??
      "";

    const specialRequestsQuery = useQuery({
      queryKey: [
        "scope-of-work-special-requests-count",
        categoryParam,
      ],
      queryFn: () =>
        fetchSpecialRequests(1, 1, {
          category: categoryParam,
        }),
      enabled: Boolean(categoryParam),
      staleTime: 1000 * 60 * 5,
      retry: 1,
    });

    const specialRequestsTotal =
      specialRequestsQuery.data?.totalItems;

    const shouldShowSpecialRequests =
      !categoryParam ||
      specialRequestsTotal === undefined ||
      specialRequestsTotal > 0;

    const stepsOrder = useMemo(() => {
      const steps: ScopeOfWorkStep[] = [
        ScopeOfWorkStep.Categories,
        ScopeOfWorkStep.TypeOfPlaces,
        ScopeOfWorkStep.TypeOfService,
        ScopeOfWorkStep.Areas,
        ScopeOfWorkStep.Objects,
      ];

      if (shouldShowSpecialRequests) {
        steps.push(ScopeOfWorkStep.SpecialRequests);
      }

      if (showSummary) {
        steps.push(ScopeOfWorkStep.Summary);
      }

      return steps;
    }, [showSummary, shouldShowSpecialRequests]);

    useEffect(() => {
      const targetStep = sessionStorage.getItem(
        "jobCreateScopeStep",
      ) as ScopeOfWorkStep | null;

      if (!targetStep) {
        return;
      }

      if (!stepsOrder.includes(targetStep)) {
        sessionStorage.removeItem(
          "jobCreateScopeStep",
        );
        return;
      }

      sessionStorage.removeItem(
        "jobCreateScopeStep",
      );

      dispatch({
        type: "SET_STEP",
        payload: targetStep,
      });
    }, [dispatch, stepsOrder]);

    useEffect(() => {
      const stateString = JSON.stringify(state);

      if (prevStateRef.current !== stateString) {
        prevStateRef.current = stateString;
        onStateChange?.(state);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const currentIndex = useMemo(
      () => stepsOrder.indexOf(state.step),
      [state.step, stepsOrder],
    );

    const isFirst = currentIndex <= 0;

    const isLast =
      currentIndex === -1 ||
      currentIndex >= stepsOrder.length - 1;

    useImperativeHandle(
      ref,
      () => ({
        next: () => {
          if (isLast) {
            return true;
          }

          if (
            state.step !== ScopeOfWorkStep.Summary
          ) {
            const validation =
              scopeOfWorkStepValidator(state);

            if (validation !== true) {
              console.warn(
                "Validación fallida:",
                validation,
              );

              return false;
            }
          }

          const nextStep =
            stepsOrder[currentIndex + 1];

          if (!nextStep) {
            return true;
          }

          dispatch({
            type: "SET_STEP",
            payload: nextStep,
          });

          return false;
        },

        prev: () => {
          if (isFirst) {
            return false;
          }

          const previousStep =
            stepsOrder[currentIndex - 1];

          if (!previousStep) {
            return false;
          }

          dispatch({
            type: "SET_STEP",
            payload: previousStep,
          });

          return true;
        },

        goTo: (step) => {
          if (!stepsOrder.includes(step)) {
            return;
          }

          dispatch({
            type: "SET_STEP",
            payload: step,
          });
        },

        reset: () => {
          dispatch({
            type: "RESET_WORKFLOW",
          });
        },

        getData: () => state,

        isValid: () => {
          if (
            state.step === ScopeOfWorkStep.Summary
          ) {
            return true;
          }

          const stepValidation =
            scopeOfWorkStepValidator(state);

          return stepValidation === true;
        },
      }),
      [
        isFirst,
        isLast,
        dispatch,
        state,
        currentIndex,
        stepsOrder,
      ],
    );

    useEffect(() => {
      onStepChange?.(state.step, isLast);
    }, [
      state.step,
      isLast,
      onStepChange,
    ]);

    return (
      <ScopeOfWorkContent
        debugJsonPayload={debugJsonPayload}
        onStepChange={onStepChange}
        showControls={showControls}
        lockCategory={lockCategory}
        showSummary={showSummary}
        job={job}
        jobAlias={jobAlias}
        setJobAlias={setJobAlias}
        jobDescription={jobDescription}
        setJobDescription={setJobDescription}
      />
    );
  },
);

ScopeOfWorkController.displayName =
  "ScopeOfWorkController";